<script setup lang="ts">
import { defineEmits, defineProps, ref, watch } from 'vue';

const props = defineProps<{
  modelValue: boolean;
  disabled: boolean;
  highlight: boolean;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
}>();

const checked = ref(props.modelValue);

watch(
    () => props.modelValue,
    (newValue) => {
      checked.value = newValue;
    }
);

watch(checked, (newValue) => {
  emit('update:modelValue', newValue);
});
</script>

<template>
  <label class="ods-switch ods-switch-size-medium ods-switch-theme-default" :class="{disabled: props.disabled ?? false, highlight: props.highlight ?? false}">
    <input type="checkbox" v-model="checked"/>
    <span class="slider">
      <span class="circle">
        <svg
            width="75%"
            height="75%"
            viewBox="0 0 9 9"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            stroke="white"
            stroke-linecap="round"
            stroke-linejoin="round">
          <path v-if="checked"
                d="M1.83374 5.60449L3.00041 6.77116L6.50041 3.10449"/>
          <path v-else
                d="M6.50041 2.604L1.83374 7.27068M1.83374 2.604L6.50041 7.27068"/>
        </svg>
      </span>
    </span>
  </label>
</template>

<style lang="scss">
.ods-switch {
  display: inline-flex;
  align-items: center;
  position: relative;
  cursor: pointer;
  border-radius: 999px;
  transition: background-color 0.3s ease;

  &.disabled {
    filter: grayscale(1);
    pointer-events: none;
  }

  input {
    opacity: 0;
    width: 0;
    height: 0;
  }

  .slider {
    position: relative;
    width: 100%;
    height: 100%;

    .circle {
      position: absolute;
      top: 0;
      left: 0;
      display: grid;
      place-items: center;
      border-radius: 50%;
      transition: transform 0.3s ease, color 0.3s ease;

      
    }

  }

  &.highlight{
    .slider {
      .circle {
        &:before, &:after{
            content: " ";
            display: block;
            border-radius: 50%;
            width: 3.5rem;
            height: 3.5rem;
            border: solid 3px var(--color-purple-500);
            position: absolute;
            z-index: 1;
        }
        &:before{
          animation: ripple-effect 2s ease-out infinite;
        }

        &:after{
            animation: ripple-effect 2s ease-out infinite 0.5s;
        }
      }
    }
  }
}

.ods-switch-size-medium {
  $radius: 1.25rem;
  $padding: 0.25rem;
  $gap: 0.25rem;
  width: $gap + 2 * $padding + 2 * $radius;
  height: 2 * $padding + $radius;
  padding: $padding;

  .slider {
    .circle {
      width: $radius;
      height: $radius;
    }
  }

  input:checked {
    + .slider {
      .circle {
        transform: translateX($gap + $radius);
      }
    }
  }
}

.ods-switch-theme-default {
  background-color: var(--color-blue-10, #E1E9FF);

  .slider .circle {
    background-color: var(--color-blue-300, #6C92FD);
    color: #fff;
  }

  &:has(input:checked) {
    background-color: var(--color-blue-10, #E1E9FF);

    .slider .circle {
      background-color: var(--color-blue-500, #0342F2);
      color: #fff;
    }
  }
}

</style>
